import React, { useEffect, useRef, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import {
  MapPin,
  Clock,
  Navigation,
  Shield,
  CheckCircle,
  ArrowRight,
  Smartphone,
  Car,
  Footprints,
  BadgeCheck,
  XCircle,
  ChevronDown,
  Mail,
  AlertTriangle
} from 'lucide-react'
import { SUPPORT_EMAIL, COMPANY_NAME, DBA_NAME, SUPPORT_PHONE, SUPPORT_PHONE_TEL } from '../constants/contact'
import { useLocalization } from '../contexts/LocalizationContext'
import { useAuth } from '../contexts/AuthContext'

// Tarifs réels facturés (identiques à SubDirectZxsem : compte NMI réglé en USD,
// on affiche donc les mêmes montants USD pour tout le monde)
const WEEKLY_PRICE = '$16.99'
const HOURLY_PRICE = '$8.90'
const SIGNUP_PATH = '/auth?auth=signup'

function SignupCta({ centered = false }: { centered?: boolean }) {
  const { t } = useLocalization()

  return (
    <div className={`w-full max-w-xl ${centered ? 'flex flex-col items-center' : ''}`}>
      <Link
        to={SIGNUP_PATH}
        className="w-full sm:w-auto min-h-[56px] px-10 rounded-2xl bg-emerald-500 hover:bg-emerald-400 active:scale-[0.98] text-white font-bold text-lg transition-all shadow-xl shadow-emerald-500/30 inline-flex items-center justify-center"
      >
        {t('landing.hero.cta')}
        <ArrowRight className="ml-2 h-5 w-5" />
      </Link>
      <p className={`mt-3 text-sm flex items-center gap-2 text-slate-400 ${centered ? 'justify-center' : ''}`}>
        <Shield className="h-4 w-4 flex-shrink-0" />
        {t('landing.hero.ctaSub')}
      </p>
    </div>
  )
}

function PhoneMockup() {
  const { t } = useLocalization()
  // Minuteur vivant : session démarrée il y a 1h14
  const [seconds, setSeconds] = useState(74 * 60 + 12)
  useEffect(() => {
    const id = setInterval(() => setSeconds((s) => s + 1), 1000)
    return () => clearInterval(id)
  }, [])
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  const timer = `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`

  return (
    <div className="pa-float relative mx-auto w-[260px] sm:w-[290px]">
      {/* Boutons physiques sur la tranche */}
      <span className="absolute -left-[3px] top-24 h-8 w-[3px] rounded-l-md bg-slate-700" aria-hidden="true" />
      <span className="absolute -left-[3px] top-36 h-14 w-[3px] rounded-l-md bg-slate-700" aria-hidden="true" />
      <span className="absolute -right-[3px] top-28 h-16 w-[3px] rounded-r-md bg-slate-700" aria-hidden="true" />
      <div className="rounded-[3rem] bg-slate-900 p-[10px] shadow-2xl shadow-emerald-900/40 ring-1 ring-white/10">
      <div className="rounded-[2.5rem] overflow-hidden bg-slate-50 aspect-[9/18.5] flex flex-col">
        {/* Barre de statut + encoche */}
        <div className="relative bg-white px-5 pt-3 pb-2 flex items-center justify-between">
          <span className="text-[11px] font-bold text-slate-900">9:41</span>
          <div className="absolute left-1/2 -translate-x-1/2 top-2 w-20 h-5 bg-slate-900 rounded-full" />
          <span className="flex items-center gap-1">
            <span className="w-3.5 h-2 rounded-[2px] bg-slate-900 inline-block" />
          </span>
        </div>
        {/* En-tête app */}
        <div className="bg-white px-4 pb-3 flex items-center justify-between border-b border-slate-100">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg bg-emerald-500 text-white font-display font-bold text-lg flex items-center justify-center">P</span>
            <div>
              <p className="text-[12px] font-bold text-slate-900 leading-none">{t('landing.phone.nearby')}</p>
              <p className="text-[10px] text-emerald-600 font-semibold mt-0.5">{t('landing.phone.spots')}</p>
            </div>
          </div>
          <MapPin className="h-4 w-4 text-slate-400" />
        </div>
        {/* Carte */}
        <div className="pa-streets relative flex-1">
          {/* Épingles P */}
          <div className="absolute left-[18%] top-[28%]">
            <span className="pa-ping-soft absolute inset-0 rounded-full bg-emerald-400" />
            <span className="relative w-6 h-6 rounded-full bg-emerald-500 text-white text-[11px] font-display font-bold flex items-center justify-center shadow-md">P</span>
          </div>
          <div className="absolute left-[62%] top-[16%]">
            <span className="relative w-6 h-6 rounded-full bg-emerald-600 text-white text-[11px] font-display font-bold flex items-center justify-center shadow-md">P</span>
          </div>
          <div className="absolute left-[74%] top-[58%]">
            <span className="pa-ping-soft absolute inset-0 rounded-full bg-emerald-400" />
            <span className="relative w-6 h-6 rounded-full bg-emerald-500 text-white text-[11px] font-display font-bold flex items-center justify-center shadow-md">P</span>
          </div>
          {/* Voiture garée */}
          <div className="absolute left-[38%] top-[62%]">
            <span className="relative w-7 h-7 rounded-full bg-slate-900 text-white flex items-center justify-center shadow-lg">
              <Car className="h-3.5 w-3.5" />
            </span>
          </div>
        </div>
        {/* Carte session */}
        <div className="bg-white p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-xl bg-slate-900 text-white flex items-center justify-center">
                <Car className="h-4 w-4" />
              </span>
              <div>
                <p className="text-[12px] font-bold text-slate-900 leading-tight">{t('landing.phone.yourCar')}</p>
                <p className="text-[10px] text-slate-500">{t('landing.phone.since')}</p>
              </div>
            </div>
            <span className="font-display text-xl font-bold text-emerald-600 tabular-nums tracking-wide">{timer}</span>
          </div>
          <div className="min-h-[42px] rounded-xl bg-emerald-500 text-white text-[13px] font-bold flex items-center justify-center gap-2">
            <Footprints className="h-4 w-4" />
            {t('landing.phone.findCar')}
          </div>
        </div>
      </div>
      </div>
    </div>
  )
}

export function Landing() {
  const { t } = useLocalization()
  const { user } = useAuth()
  const heroRef = useRef<HTMLElement>(null)
  const [showStickyCta, setShowStickyCta] = useState(false)

  useEffect(() => {
    const hero = heroRef.current
    if (!hero || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyCta(!entry.isIntersecting),
      { rootMargin: '-40px 0px 0px 0px' }
    )
    observer.observe(hero)
    return () => observer.disconnect()
  }, [])

  // Si l'utilisateur est connecté, rediriger vers le dashboard
  if (user) {
    return <Navigate to="/dashboard" replace />
  }

  const featureItems = [
    { key: 'smartMap', icon: MapPin },
    { key: 'favorites', icon: Car },
    { key: 'timer', icon: Clock },
    { key: 'navigation', icon: Navigation },
    { key: 'plate', icon: BadgeCheck },
    { key: 'web', icon: Smartphone },
  ] as const

  return (
    <div className="min-h-screen bg-white font-body text-slate-900">

      {/* ── Hero : asphalte de nuit ─────────────────────────────────── */}
      <section ref={heroRef} className="pa-asphalt relative overflow-hidden">
        {/* Header */}
        <header className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="w-9 h-9 rounded-xl bg-emerald-500 text-white font-display font-bold text-2xl flex items-center justify-center flex-shrink-0">P</span>
            <span className="font-display font-bold text-2xl text-white tracking-wide truncate">Parks Avenue</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            <Link to="/auth" className="text-slate-300 hover:text-white font-semibold text-sm sm:text-base px-2 py-2 transition-colors">
              {t('common.signIn')}
            </Link>
            <Link
              to={SIGNUP_PATH}
              className="hidden sm:inline-flex min-h-[44px] items-center px-5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white font-bold text-sm transition-colors"
            >
              {t('common.getStarted')}
            </Link>
          </div>
        </header>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-16 sm:pt-16 sm:pb-24 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          <div>
            <p className="pa-fade-up pa-d1 inline-flex items-center gap-2 text-emerald-400 font-display font-semibold text-lg uppercase tracking-[0.2em] mb-5">
              <span className="w-8 pa-lane text-emerald-400" aria-hidden="true" />
              {t('landing.hero.eyebrow')}
            </p>
            <h1 className="pa-fade-up pa-d2 font-display font-bold uppercase text-white leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-7xl mb-6">
              {t('landing.hero.titleA')}
              <span className="block text-emerald-400">{t('landing.hero.titleB')}</span>
            </h1>
            <p className="pa-fade-up pa-d3 text-slate-300 text-base sm:text-lg leading-relaxed max-w-xl mb-8">
              {t('landing.hero.subtitle')}
            </p>
            <div className="pa-fade-up pa-d4">
              <SignupCta />
            </div>
            {/* Chips de confiance */}
            <ul className="pa-fade-up pa-d5 mt-8 flex flex-wrap gap-x-5 gap-y-2 text-[13px] text-slate-400">
              {(['secure', 'cancel', 'noApp', 'support'] as const).map((k) => (
                <li key={k} className="flex items-center gap-1.5">
                  <CheckCircle className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                  {t(`landing.trust.${k}`)}
                </li>
              ))}
            </ul>
          </div>
          <div className="pa-fade-up pa-d4 relative">
            <PhoneMockup />
          </div>
        </div>
        <div className="pa-lane text-amber-400 relative z-10" aria-hidden="true" />
      </section>

      {/* ── Panneau "bon à savoir" (honnêteté avant inscription) ────── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 -mt-0 py-8 sm:py-10">
        <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-5 sm:p-6 flex items-start gap-4">
          <span className="w-10 h-10 rounded-xl bg-amber-400 text-slate-900 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="h-5 w-5" />
          </span>
          <div>
            <h2 className="font-display font-bold uppercase tracking-wide text-lg text-slate-900 mb-1">
              {t('landing.notice.title')}
            </h2>
            <p className="text-slate-700 text-[15px] leading-relaxed">{t('landing.notice.text')}</p>
          </div>
        </div>
      </section>

      {/* ── Comment ça marche ───────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <div className="mb-10 sm:mb-14">
          <h2 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl text-slate-900">
            {t('landing.how.title')}
          </h2>
          <p className="text-slate-500 text-base sm:text-lg mt-2">{t('landing.how.subtitle')}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {(['step1', 'step2', 'step3'] as const).map((step, i) => (
            <div key={step} className="relative rounded-2xl border border-slate-200 bg-white p-6 hover:border-emerald-300 hover:shadow-lg hover:shadow-emerald-100/60 transition-all">
              <span className="font-display font-bold text-6xl text-slate-100 absolute top-3 right-5 select-none" aria-hidden="true">
                {i + 1}
              </span>
              <span className="relative w-11 h-11 rounded-xl bg-slate-900 text-emerald-400 font-display font-bold text-2xl flex items-center justify-center mb-4">
                {i + 1}
              </span>
              <h3 className="relative font-bold text-lg text-slate-900 mb-2">{t(`landing.how.${step}.title`)}</h3>
              <p className="relative text-slate-600 text-[15px] leading-relaxed">{t(`landing.how.${step}.desc`)}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="pa-lane text-slate-300 max-w-6xl mx-auto" aria-hidden="true" />

      {/* ── Fonctionnalités réelles ─────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <div className="mb-10 sm:mb-14 max-w-2xl">
          <h2 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl text-slate-900">
            {t('landing.features.title')}
          </h2>
          <p className="text-slate-500 text-base sm:text-lg mt-2">{t('landing.features.subtitle')}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {featureItems.map(({ key, icon: Icon }) => (
            <div key={key} className="rounded-2xl bg-slate-50 border border-slate-100 p-6">
              <span className="w-11 h-11 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center mb-4">
                <Icon className="h-5 w-5" />
              </span>
              <h3 className="font-bold text-lg text-slate-900 mb-1.5">{t(`landing.features.${key}.title`)}</h3>
              <p className="text-slate-600 text-[15px] leading-relaxed">{t(`landing.features.${key}.description`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Tarifs (montants réels, USD) ────────────────────────────── */}
      <section className="bg-slate-50 border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
          <div className="mb-10 sm:mb-14 text-center max-w-2xl mx-auto">
            <h2 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl text-slate-900">
              {t('landing.pricing.title')}
            </h2>
            <p className="text-slate-500 text-base sm:text-lg mt-2">{t('landing.pricing.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 max-w-3xl mx-auto items-stretch">
            {/* Hebdo illimité — plan principal */}
            <div className="relative rounded-3xl bg-slate-900 text-white p-7 sm:p-8 shadow-2xl shadow-slate-900/20 flex flex-col">
              <span className="absolute -top-3.5 left-6 px-3 py-1 rounded-full bg-emerald-500 text-white text-xs font-bold uppercase tracking-wider">
                {t('landing.pricing.mostPopular')}
              </span>
              <p className="font-display font-semibold uppercase tracking-[0.15em] text-emerald-400 text-lg mb-3">
                {t('landing.pricing.weekly.name')}
              </p>
              <p className="flex items-baseline gap-2 mb-6">
                <span className="font-display font-bold text-6xl tracking-tight">{WEEKLY_PRICE}</span>
                <span className="text-slate-400 font-medium">{t('landing.pricing.weekly.per')}</span>
              </p>
              <ul className="space-y-3 mb-8 flex-1">
                {(['f1', 'f2', 'f3', 'f4'] as const).map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-[15px] text-slate-200">
                    <CheckCircle className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    {t(`landing.pricing.weekly.${f}`)}
                  </li>
                ))}
              </ul>
              <Link
                to={SIGNUP_PATH}
                className="min-h-[52px] rounded-xl bg-emerald-500 hover:bg-emerald-400 active:scale-[0.98] text-white font-bold flex items-center justify-center transition-all"
              >
                {t('landing.hero.cta')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>

            {/* Pay as you go */}
            <div className="rounded-3xl bg-white border-2 border-slate-200 p-7 sm:p-8 flex flex-col">
              <p className="font-display font-semibold uppercase tracking-[0.15em] text-slate-500 text-lg mb-3">
                {t('landing.pricing.payg.name')}
              </p>
              <p className="flex items-baseline gap-2 mb-6">
                <span className="text-slate-500 font-medium">{t('landing.pricing.payg.from')}</span>
                <span className="font-display font-bold text-6xl tracking-tight text-slate-900">{HOURLY_PRICE}</span>
                <span className="text-slate-500 font-medium">{t('landing.pricing.payg.per')}</span>
              </p>
              <ul className="space-y-3 mb-8 flex-1">
                {(['f1', 'f2', 'f3'] as const).map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-[15px] text-slate-700">
                    <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    {t(`landing.pricing.payg.${f}`)}
                  </li>
                ))}
              </ul>
              <Link
                to={SIGNUP_PATH}
                className="min-h-[52px] rounded-xl border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white active:scale-[0.98] font-bold flex items-center justify-center transition-all"
              >
                {t('landing.pricing.cta')}
              </Link>
            </div>
          </div>

          <p className="text-center text-slate-500 text-sm max-w-2xl mx-auto mt-8 leading-relaxed">
            {t('landing.pricing.note')}
          </p>
        </div>
      </section>

      {/* ── Parks Avenue vs apps de paiement d'horodateur ───────────── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <div className="mb-8 sm:mb-12 max-w-3xl">
          <h2 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl text-slate-900 mb-3">
            {t('landing.compare.title')}
          </h2>
          <p className="text-slate-600 text-base sm:text-lg leading-relaxed">{t('landing.compare.intro')}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-7">
            <p className="font-bold text-slate-900">{t('landing.compare.them.title')}</p>
            <p className="text-sm text-slate-500 mb-5">{t('landing.compare.them.sub')}</p>
            <ul className="space-y-3">
              {(['i1', 'i2', 'i3'] as const).map((i) => (
                <li key={i} className="flex items-start gap-2.5 text-[15px] text-slate-600">
                  <XCircle className="h-5 w-5 text-slate-300 flex-shrink-0 mt-0.5" />
                  {t(`landing.compare.them.${i}`)}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border-2 border-emerald-500 bg-emerald-50/40 p-6 sm:p-7">
            <p className="font-bold text-slate-900 flex items-center gap-2">
              <span className="w-6 h-6 rounded-md bg-emerald-500 text-white font-display font-bold text-sm flex items-center justify-center">P</span>
              {t('landing.compare.us.title')}
            </p>
            <p className="text-sm text-emerald-700 mb-5">{t('landing.compare.us.sub')}</p>
            <ul className="space-y-3">
              {(['i1', 'i2', 'i3'] as const).map((i) => (
                <li key={i} className="flex items-start gap-2.5 text-[15px] text-slate-700">
                  <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  {t(`landing.compare.us.${i}`)}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <p className="text-xs text-slate-400 leading-relaxed max-w-4xl mt-6">
          {t('landing.compare.disclaimer')}
        </p>
      </section>

      <div className="pa-lane text-slate-300 max-w-6xl mx-auto" aria-hidden="true" />

      {/* ── FAQ (details/summary natif : accessible + indexable) ────── */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <h2 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl text-slate-900 mb-8 sm:mb-12">
          {t('landing.faq.title')}
        </h2>
        <div className="space-y-3">
          {(['q1', 'q2', 'q3', 'q4', 'q5'] as const).map((q) => (
            <details key={q} className="group rounded-2xl border border-slate-200 bg-white open:border-emerald-300 open:shadow-md open:shadow-emerald-100/50 transition-all">
              <summary className="flex items-center justify-between gap-4 cursor-pointer list-none px-5 py-4 min-h-[56px] font-semibold text-slate-900 text-[15px] sm:text-base [&::-webkit-details-marker]:hidden">
                {t(`landing.faq.${q}.q`)}
                <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0 transition-transform group-open:rotate-180" />
              </summary>
              <p className="px-5 pb-5 text-slate-600 text-[15px] leading-relaxed">{t(`landing.faq.${q}.a`)}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ── CTA final ───────────────────────────────────────────────── */}
      <section className="pa-asphalt relative overflow-hidden">
        <div className="pa-lane text-amber-400" aria-hidden="true" />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
          <h2 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-6xl text-white leading-[0.95] mb-4">
            {t('landing.finalCta.title')}
          </h2>
          <p className="text-slate-300 text-base sm:text-lg mb-8 max-w-xl mx-auto">{t('landing.finalCta.subtitle')}</p>
          <div className="flex justify-center">
            <SignupCta centered />
          </div>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="inline-flex items-center gap-2 mt-8 text-slate-400 hover:text-white text-sm font-medium transition-colors"
          >
            <Mail className="h-4 w-4" />
            {t('footer.contactSupport')}
          </a>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <footer className="bg-slate-950 text-white pt-12 pb-28 md:pb-12 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <span className="w-8 h-8 rounded-lg bg-emerald-500 text-white font-display font-bold text-xl flex items-center justify-center">P</span>
                <span className="font-display font-bold text-xl tracking-wide">Parks Avenue</span>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed mb-4 max-w-sm">{t('footer.description')}</p>
              <p className="text-slate-500 text-xs leading-relaxed max-w-sm">{t('footer.independence')}</p>
            </div>
            <div>
              <h4 className="font-display font-semibold uppercase tracking-wider text-slate-300 mb-4">{t('footer.legal')}</h4>
              <div className="space-y-2 text-sm">
                <Link to="/terms" className="text-slate-400 hover:text-white block transition-colors py-1">{t('navigation.terms')}</Link>
                <Link to="/privacy" className="text-slate-400 hover:text-white block transition-colors py-1">{t('navigation.privacy')}</Link>
                <Link to="/refund" className="text-slate-400 hover:text-white block transition-colors py-1">Refund Policy</Link>
              </div>
            </div>
            <div>
              <h4 className="font-display font-semibold uppercase tracking-wider text-slate-300 mb-4">{t('footer.support')}</h4>
              <div className="space-y-2 text-sm">
                <Link to="/contact" className="text-slate-400 hover:text-white block transition-colors py-1">Contact</Link>
                <a href={`tel:${SUPPORT_PHONE_TEL}`} className="text-slate-400 hover:text-white block transition-colors py-1">{SUPPORT_PHONE}</a>
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-slate-400 hover:text-white block transition-colors py-1">{t('footer.contactSupport')}</a>
                <Link to="/auth" className="text-slate-400 hover:text-white block transition-colors py-1">{t('common.signIn')}</Link>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 mt-10 pt-8 text-center space-y-2">
            <p className="text-slate-400 text-sm">© 2026 {COMPANY_NAME}. {t('footer.allRightsReserved')}</p>
            <p className="text-slate-500 text-xs">{DBA_NAME} is a registered trade name of {COMPANY_NAME}.</p>
            <p className="text-slate-500 text-xs">
              {t('footer.needHelp')}{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-emerald-400 hover:text-emerald-300 transition-colors">{SUPPORT_EMAIL}</a>
            </p>
          </div>
        </div>
      </footer>

      {/* ── Barre CTA fixe mobile ───────────────────────────────────── */}
      {showStickyCta && (
        <div className="pa-slide-in-bar fixed bottom-0 inset-x-0 z-50 md:hidden bg-white/95 backdrop-blur border-t border-slate-200 px-4 pt-3 shadow-[0_-8px_30px_rgba(2,6,23,0.12)]"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        >
          <Link
            to={SIGNUP_PATH}
            className="min-h-[52px] rounded-xl bg-emerald-500 active:scale-[0.98] text-white font-bold text-base flex items-center justify-center transition-transform"
          >
            {t('landing.sticky.cta')}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      )}
    </div>
  )
}
